import { useState } from 'react';
import { produce } from 'immer';
import { NumberInput } from '@hungryroot/pho';
import { Heading } from '@hungryroot/ramen';
import {
  type PricingServiceRecipeQuantity,
  type RecipeQuantityPricingOption,
} from '@hungryroot/rest';
import { type PricingConfig } from '@hungryroot/rest/api/get_pricing_config/get';
import { StyledSheetOrModal } from '../StyledSheetOrModal/StyledSheetOrModal';

import styles from './EditRecipeNumberModal.module.css';

interface EditRecipeNumberModalProps {
  isOpen: boolean;
  pricePerServing?: number;
  onSubmit(values: EditRecipeNumberModalProps['initialValues']): void;
  initialValues: {
    dinnerRecipes: number;
    lunchRecipes: number;
  };
  onCancel(): void;
  onClick: ({
    numLunch,
    numDinner,
  }: {
    numDinner: number;
    numLunch: number;
  }) => Promise<
    | PricingServiceRecipeQuantity
    | PricingConfig
    | RecipeQuantityPricingOption
    | undefined
  >;
  isLoading?: boolean;
  maxRecipes: number;
  minRecipes: number;
  isSubmitting?: boolean;
  confirmButtonText: string;
}

export function EditRecipeNumberModal({
  onSubmit,
  initialValues,
  isOpen,
  pricePerServing,
  onCancel,
  onClick,
  isLoading,
  isSubmitting,
  maxRecipes,
  minRecipes,
  confirmButtonText,
}: EditRecipeNumberModalProps) {
  const [values, setValues] = useState(initialValues);
  const { dinnerRecipes, lunchRecipes } = values;
  const total = lunchRecipes + dinnerRecipes;
  const [effectivePricePerServing, setEffectivePricingPerServing] =
    useState(pricePerServing);

  async function updateValue({
    dinnerRecipes,
    lunchRecipes,
  }: EditRecipeNumberModalProps['initialValues']) {
    setValues({ dinnerRecipes, lunchRecipes });

    const response = await onClick({
      numDinner: dinnerRecipes,
      numLunch: lunchRecipes,
    });

    setEffectivePricingPerServing(Number(response?.price_per_serving));
  }

  return (
    <StyledSheetOrModal
      testid="edit-recipe-number"
      onCancel={onCancel}
      onAdd={() => {
        onSubmit(values);
      }}
      bodyText={`You can add more groceries and recipes later. You have a max of ${maxRecipes} recipes per delivery.`}
      heading="Edit # of recipes per week"
      confirmButtonText={confirmButtonText}
      ariaLabel="Edit # of recipes per week"
      isOpen={isOpen}
      isSubmitting={isSubmitting}
    >
      <div>
        <NumberInput
          isLoading={isLoading}
          className={styles.lunch}
          buttonsClassName={styles.buttons}
          label="Lunch recipes"
          value={lunchRecipes}
          minValue={Math.max(minRecipes - dinnerRecipes, 0)}
          maxValue={maxRecipes - dinnerRecipes}
          onChange={(next) => {
            updateValue(
              produce(values, (draft) => {
                draft.lunchRecipes = next;
              })
            );
          }}
        />
        <NumberInput
          isLoading={isLoading}
          className={styles.dinner}
          buttonsClassName={styles.buttons}
          label="Dinner recipes"
          value={dinnerRecipes}
          minValue={Math.max(minRecipes - lunchRecipes, 0)}
          maxValue={maxRecipes - lunchRecipes}
          onChange={(next) => {
            updateValue(
              produce(values, (draft) => {
                draft.dinnerRecipes = next;
              })
            );
          }}
        />
        <div className={styles.row}>
          <Heading variant="H6">Total</Heading>
          {effectivePricePerServing && (
            <div>{`${total} recipes ($${effectivePricePerServing}/serving)`}</div>
          )}
        </div>
      </div>
    </StyledSheetOrModal>
  );
}
